import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { collection, query, orderBy, getDocs, addDoc, Timestamp, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import VoteOnBehalfButton from './VoteOnBehalfButton';
import Markdown from '../Markdown';
import {
  X,
  ChevronUp,
  Calendar,
  User,
  MessageSquare,
  StickyNote,
  Send,
  Loader2,
  ExternalLink,
  Trash2,
  Edit,
  Save,
  XCircle
} from 'lucide-react';

interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  voteCount: number;
  createdBy: string;
  createdByEmail: string;
  createdByNickname?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  imageUrls?: string[];
}

interface Comment {
  id: string;
  userId: string;
  userEmail: string;
  userNickname?: string;
  text: string;
  createdAt: Timestamp;
}

interface InternalNote {
  id: string;
  authorId: string;
  authorEmail: string;
  note: string;
  createdAt: Timestamp;
}

interface Vote {
  id: string;
  userId: string;
  userEmail: string;
  userNickname?: string;
  createdAt: Timestamp;
}

interface RequestDetailModalProps {
  request: FeatureRequest;
  customerId: string;
  customerSlug: string;
  brandColor: string;
  onClose: () => void;
  onStatusChange: (requestId: string, newStatus: string) => void;
  onDelete?: (requestId: string) => void;
}

export default function RequestDetailModal({
  request,
  customerId,
  customerSlug,
  brandColor,
  onClose,
  onStatusChange,
  onDelete,
}: RequestDetailModalProps) {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [internalNotes, setInternalNotes] = useState<InternalNote[]>([]);
  const [votes, setVotes] = useState<Vote[]>([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [loadingVotes, setLoadingVotes] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const [deletingVoteId, setDeletingVoteId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'comments' | 'notes' | 'voters'>('details');
  const [deletingRequest, setDeletingRequest] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedTitle, setEditedTitle] = useState(request.title);
  const [editedDescription, setEditedDescription] = useState(request.description);
  const [editedStatus, setEditedStatus] = useState(request.status);
  const [editedCategory, setEditedCategory] = useState(request.category);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchComments();
    fetchInternalNotes();
    fetchVotes();
  }, [customerId, request.id]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const commentsRef = collection(db, 'customers', customerId, 'requests', request.id, 'comments');
      const q = query(commentsRef, orderBy('createdAt', 'asc'));
      const snapshot = await getDocs(q);

      const commentsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Comment[];

      setComments(commentsList);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const fetchInternalNotes = async () => {
    setLoadingNotes(true);
    try {
      const notesRef = collection(db, 'customers', customerId, 'requests', request.id, 'internalNotes');
      const q = query(notesRef, orderBy('createdAt', 'asc'));
      const snapshot = await getDocs(q);

      const notesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as InternalNote[];

      setInternalNotes(notesList);
    } catch (error) {
      console.error('Error fetching internal notes:', error);
    } finally {
      setLoadingNotes(false);
    }
  };

  const fetchVotes = async () => {
    setLoadingVotes(true);
    try {
      const votesRef = collection(db, 'customers', customerId, 'requests', request.id, 'votes');
      const q = query(votesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const votesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Vote[];

      setVotes(votesList);
    } catch (error) {
      console.error('Error fetching votes:', error);
    } finally {
      setLoadingVotes(false);
    }
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !newNote.trim()) return;

    setSubmittingNote(true);
    try {
      await addDoc(
        collection(db, 'customers', customerId, 'requests', request.id, 'internalNotes'),
        {
          authorId: user.uid,
          authorEmail: user.email || 'Unknown',
          note: newNote.trim(),
          createdAt: Timestamp.now(),
        }
      );

      // Refetch notes
      await fetchInternalNotes();
      setNewNote('');
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note. Please try again.');
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!user) return;

    const confirmed = window.confirm('Are you sure you want to delete this internal note?');
    if (!confirmed) return;

    setDeletingNoteId(noteId);
    try {
      await deleteDoc(doc(db, 'customers', customerId, 'requests', request.id, 'internalNotes', noteId));

      // Update local state
      setInternalNotes(prev => prev.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note. Please try again.');
    } finally {
      setDeletingNoteId(null);
    }
  };

  const handleDeleteVote = async (voteId: string, userEmail: string) => {
    if (!user) return;

    const confirmed = window.confirm(`Are you sure you want to delete the vote from ${userEmail}? The user will NOT be notified.`);
    if (!confirmed) return;

    setDeletingVoteId(voteId);
    try {
      await deleteDoc(doc(db, 'customers', customerId, 'requests', request.id, 'votes', voteId));

      // Update local state
      setVotes(prev => prev.filter(vote => vote.id !== voteId));
      toast.success('Vote deleted successfully');
    } catch (error) {
      console.error('Error deleting vote:', error);
      toast.error('Failed to delete vote. Please try again.');
    } finally {
      setDeletingVoteId(null);
    }
  };

  const handleDeleteRequest = async () => {
    if (!confirm(`Are you sure you want to delete "${request.title}"? This action cannot be undone and will also delete all votes, comments, and internal notes associated with this request.`)) {
      return;
    }

    setDeletingRequest(true);
    try {
      // Delete all subcollections first
      // Delete votes subcollection
      const votesRef = collection(db, 'customers', customerId, 'requests', request.id, 'votes');
      const votesSnapshot = await getDocs(votesRef);
      const voteDeletePromises = votesSnapshot.docs.map(voteDoc => deleteDoc(voteDoc.ref));
      await Promise.all(voteDeletePromises);

      // Delete comments subcollection
      const commentsRef = collection(db, 'customers', customerId, 'requests', request.id, 'comments');
      const commentsSnapshot = await getDocs(commentsRef);
      const commentDeletePromises = commentsSnapshot.docs.map(commentDoc => deleteDoc(commentDoc.ref));
      await Promise.all(commentDeletePromises);

      // Delete internal notes subcollection
      const notesRef = collection(db, 'customers', customerId, 'requests', request.id, 'internalNotes');
      const notesSnapshot = await getDocs(notesRef);
      const noteDeletePromises = notesSnapshot.docs.map(noteDoc => deleteDoc(noteDoc.ref));
      await Promise.all(noteDeletePromises);

      // Finally delete the request itself
      await deleteDoc(doc(db, 'customers', customerId, 'requests', request.id));

      toast.success('Request deleted successfully');

      // Call the parent's onDelete callback if provided
      if (onDelete) {
        onDelete(request.id);
      }

      // Close the modal
      onClose();
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error('Failed to delete request. Please try again.');
    } finally {
      setDeletingRequest(false);
    }
  };

  const handleStartEdit = () => {
    setIsEditMode(true);
    setEditedTitle(request.title);
    setEditedDescription(request.description);
    setEditedStatus(request.status);
    setEditedCategory(request.category);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedTitle(request.title);
    setEditedDescription(request.description);
    setEditedStatus(request.status);
    setEditedCategory(request.category);
  };

  const handleSaveEdit = async () => {
    if (!editedTitle.trim()) {
      toast.error('Title cannot be empty');
      return;
    }

    if (!editedDescription.trim()) {
      toast.error('Description cannot be empty');
      return;
    }

    setIsSaving(true);
    try {
      // Update the request with adminEdit flag to prevent notifications
      await updateDoc(doc(db, 'customers', customerId, 'requests', request.id), {
        title: editedTitle.trim(),
        description: editedDescription.trim(),
        status: editedStatus,
        category: editedCategory,
        updatedAt: Timestamp.now(),
        adminEdit: true, // Flag to indicate this is an admin edit (prevents notifications)
      });

      // Update the local request object
      request.title = editedTitle.trim();
      request.description = editedDescription.trim();
      request.status = editedStatus;
      request.category = editedCategory;
      request.updatedAt = Timestamp.now();

      toast.success('Request updated successfully');
      setIsEditMode(false);
    } catch (error) {
      console.error('Error updating request:', error);
      toast.error('Failed to update request. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center gap-2" style={{ color: brandColor }}>
                  <ChevronUp className="h-5 w-5" />
                  <span className="font-bold text-lg">{request.voteCount}</span>
                </div>
                {isEditMode ? (
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="text-2xl font-bold text-foreground bg-background border border-input rounded px-2 py-1 flex-1"
                    placeholder="Request title"
                  />
                ) : (
                  <h2 className="text-2xl font-bold text-foreground">{request.title}</h2>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {isEditMode ? (
                  <>
                    <select
                      value={editedStatus}
                      onChange={(e) => setEditedStatus(e.target.value)}
                      className={`text-sm font-medium px-3 py-1.5 rounded-full capitalize cursor-pointer border-0 ${getStatusColor(editedStatus)}`}
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    <select
                      value={editedCategory}
                      onChange={(e) => setEditedCategory(e.target.value)}
                      className="px-3 py-1.5 bg-muted rounded-full text-sm capitalize cursor-pointer border border-input"
                    >
                      <option value="feature_request">Feature Request</option>
                      <option value="bug_report">Bug Report</option>
                      <option value="improvement">Improvement</option>
                      <option value="other">Other</option>
                    </select>
                  </>
                ) : (
                  <>
                    <select
                      value={request.status}
                      onChange={(e) => onStatusChange(request.id, e.target.value)}
                      className={`text-sm font-medium px-3 py-1.5 rounded-full capitalize cursor-pointer border-0 ${getStatusColor(request.status)}`}
                      disabled={isEditMode}
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    <span className="px-3 py-1 bg-muted rounded-full text-sm capitalize">
                      {request.category.replace('_', ' ')}
                    </span>
                  </>
                )}
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  {new Date(request.createdAt.toDate()).toLocaleDateString()}
                </span>
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  {request.createdByNickname || request.createdByEmail}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-6 border-b border-border">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-current text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              style={activeTab === 'details' ? { borderColor: brandColor, color: brandColor } : {}}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('comments')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'comments'
                  ? 'border-current text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              style={activeTab === 'comments' ? { borderColor: brandColor, color: brandColor } : {}}
            >
              <MessageSquare className="h-4 w-4" />
              Comments ({comments.length})
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'notes'
                  ? 'border-current text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              style={activeTab === 'notes' ? { borderColor: brandColor, color: brandColor } : {}}
            >
              <StickyNote className="h-4 w-4" />
              Internal Notes ({internalNotes.length})
            </button>
            <button
              onClick={() => setActiveTab('voters')}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'voters'
                  ? 'border-current text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              style={activeTab === 'voters' ? { borderColor: brandColor, color: brandColor } : {}}
            >
              <ChevronUp className="h-4 w-4" />
              Voters ({votes.length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Edit Mode Buttons */}
              {isEditMode && (
                <div className="flex items-center gap-3 pb-4 border-b border-border">
                  <Button
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                    style={{ backgroundColor: brandColor }}
                    className="text-white"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Changes will be saved without notifying users
                  </span>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Description</h3>
                {isEditMode ? (
                  <textarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    rows={10}
                    className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Enter request description..."
                  />
                ) : (
                  <div className="text-muted-foreground">
                    <Markdown content={request.description} />
                  </div>
                )}

                {/* Image Attachments */}
                {request.imageUrls && request.imageUrls.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-foreground mb-2">Attachments</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {request.imageUrls.map((url, index) => (
                        <a
                          key={index}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group"
                        >
                          <img
                            src={url}
                            alt={`Attachment ${index + 1}`}
                            className="w-full h-48 object-cover rounded border border-border group-hover:opacity-80 transition-opacity cursor-pointer"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-border pt-4">
                <h3 className="text-sm font-semibold text-foreground mb-3">Admin Actions</h3>
                <div className="flex items-center gap-3 flex-wrap">
                  {!isEditMode && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleStartEdit}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Request
                    </Button>
                  )}
                  <VoteOnBehalfButton
                    customerId={customerId}
                    requestId={request.id}
                    requestTitle={request.title}
                    brandColor={brandColor}
                    onSuccess={() => {
                      // Could refresh vote count or show success message
                      console.log('Vote cast successfully');
                    }}
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDeleteRequest}
                    disabled={deletingRequest || isEditMode}
                  >
                    {deletingRequest ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Request
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div>
                <a
                  href={`/${customerSlug}#request-${request.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  View on public portal
                </a>
              </div>
            </div>
          )}

          {activeTab === 'comments' && (
            <div className="space-y-4">
              {loadingComments ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" style={{ color: brandColor }} />
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No public comments yet</p>
                </div>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: brandColor }}
                      >
                        {(comment.userNickname || comment.userEmail).charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm text-foreground">
                            {comment.userNickname || comment.userEmail}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(comment.createdAt.toDate()).toLocaleString()}
                          </span>
                        </div>
                        <div className="text-sm text-foreground">
                          <Markdown content={comment.text} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              {/* Add Note Form */}
              <form onSubmit={handleAddNote} className="border border-border rounded-lg p-4 bg-muted/50">
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Add Internal Note
                </label>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add notes that are only visible to admins..."
                  rows={3}
                  className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary mb-3"
                  disabled={submittingNote}
                />
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={!newNote.trim() || submittingNote}
                    style={{ backgroundColor: brandColor }}
                    className="text-white"
                  >
                    {submittingNote ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Add Note
                      </>
                    )}
                  </Button>
                </div>
              </form>

              {/* Notes List */}
              {loadingNotes ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" style={{ color: brandColor }} />
                </div>
              ) : internalNotes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <StickyNote className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No internal notes yet</p>
                  <p className="text-sm mt-1">Add notes to track discussions and decisions</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {internalNotes.map((note) => (
                    <div
                      key={note.id}
                      className="border border-border rounded-lg p-4"
                      style={{ backgroundColor: `${brandColor}05` }}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ backgroundColor: brandColor }}
                        >
                          {note.authorEmail.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm text-foreground">
                                {note.authorEmail}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(note.createdAt.toDate()).toLocaleString()}
                              </span>
                            </div>
                            <button
                              onClick={() => handleDeleteNote(note.id)}
                              disabled={deletingNoteId === note.id}
                              className="text-muted-foreground hover:text-destructive transition-colors p-1"
                              title="Delete note"
                            >
                              {deletingNoteId === note.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <p className="text-sm text-foreground whitespace-pre-wrap">{note.note}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'voters' && (
            <div className="space-y-4">
              {/* Voters List */}
              {loadingVotes ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" style={{ color: brandColor }} />
                </div>
              ) : votes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ChevronUp className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No votes yet</p>
                  <p className="text-sm mt-1">Be the first to vote or vote on behalf of a user</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {votes.map((vote) => (
                    <div
                      key={vote.id}
                      className="border border-border rounded-lg p-4 bg-card"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                          style={{ backgroundColor: brandColor }}
                        >
                          {(vote.userNickname || vote.userEmail || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-foreground">
                              {vote.userNickname || vote.userEmail || 'Unknown User'}
                            </span>
                            {vote.userNickname && vote.userEmail && (
                              <span className="text-xs text-muted-foreground">
                                ({vote.userEmail})
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            Voted {new Date(vote.createdAt.toDate()).toLocaleString()}
                          </span>
                        </div>
                        {vote.userId === request.createdBy ? (
                          <div
                            className="text-muted-foreground/50 p-2"
                            title="Creator's vote cannot be deleted"
                          >
                            <Trash2 className="h-4 w-4 opacity-50" />
                          </div>
                        ) : (
                          <button
                            onClick={() => handleDeleteVote(vote.id, vote.userEmail || 'Unknown User')}
                            disabled={deletingVoteId === vote.id}
                            className="text-muted-foreground hover:text-destructive transition-colors p-2"
                            title="Delete vote (user will NOT be notified)"
                          >
                            {deletingVoteId === vote.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
